"use client";

import { c as _c } from "react/compiler-runtime";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { createContext, useContext, useMemo } from "react";
import { useIsClient } from "../hooks/use-is-client";
import { usePointerCapability } from "../hooks/use-pointer-capability";
import { cn } from "../utils";
const ResponsiveTooltipContext = createContext<{
  isTouchDevice: boolean;
} | null>(null);
function ResponsiveTooltip(t0) {
  const $ = _c(10);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    isTouchDevice
  } = usePointerCapability();
  const mounted = useIsClient();
  const isConfirmedTouchDevice = mounted && isTouchDevice;
  let t1;
  if ($[2] !== isConfirmedTouchDevice) {
    t1 = {
      isTouchDevice: isConfirmedTouchDevice
    };
    $[2] = isConfirmedTouchDevice;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const contextValue = t1;
  let t2;
  if ($[4] !== isConfirmedTouchDevice || $[5] !== props) {
    t2 = isConfirmedTouchDevice ? <PopoverPrimitive.Root data-slot="responsive-tooltip" {...props} /> : <TooltipPrimitive.Provider delay={0}><TooltipPrimitive.Root data-slot="responsive-tooltip" {...props} /></TooltipPrimitive.Provider>;
    $[4] = isConfirmedTouchDevice;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== contextValue || $[8] !== t2) {
    t3 = <ResponsiveTooltipContext.Provider value={contextValue}>{t2}</ResponsiveTooltipContext.Provider>;
    $[7] = contextValue;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function ResponsiveTooltipTrigger(t0) {
  const $ = _c(12);
  let closeDelay;
  let nativeButton;
  let props;
  if ($[0] !== t0) {
    ({
      nativeButton,
      closeDelay,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = closeDelay;
    $[2] = nativeButton;
    $[3] = props;
  } else {
    closeDelay = $[1];
    nativeButton = $[2];
    props = $[3];
  }
  const ctx = useContext(ResponsiveTooltipContext);
  if (!ctx) {
    throw new Error("ResponsiveTooltipTrigger must be used within <ResponsiveTooltip>.");
  }
  const {
    Trigger
  } = ctx.isTouchDevice ? PopoverPrimitive : TooltipPrimitive;
  const tooltipCloseDelay = closeDelay ?? (ctx.isTouchDevice ? undefined : 150);
  let t1;
  if ($[4] !== ctx.isTouchDevice || $[5] !== nativeButton || $[6] !== tooltipCloseDelay) {
    t1 = ctx.isTouchDevice ? {
      nativeButton,
      openOnHover: true
    } satisfies PopoverPrimitive.Trigger.Props : {
      closeDelay: tooltipCloseDelay
    } satisfies TooltipPrimitive.Trigger.Props;
    $[4] = ctx.isTouchDevice;
    $[5] = nativeButton;
    $[6] = tooltipCloseDelay;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const triggerProps = t1;
  let t2;
  if ($[8] !== Trigger || $[9] !== props || $[10] !== triggerProps) {
    t2 = <Trigger data-slot="responsive-tooltip-trigger" {...props} {...triggerProps} />;
    $[8] = Trigger;
    $[9] = props;
    $[10] = triggerProps;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function ResponsiveTooltipContent(t0) {
  const $ = _c(30);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      className,
      side: t1,
      sideOffset: t2,
      align: t3,
      alignOffset: t4,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const side = t1 === undefined ? "top" : t1;
  const sideOffset = t2 === undefined ? 4 : t2;
  const align = t3 === undefined ? "center" : t3;
  const alignOffset = t4 === undefined ? 0 : t4;
  const ctx = useContext(ResponsiveTooltipContext);
  if (!ctx) {
    throw new Error("ResponsiveTooltipContent must be used within <ResponsiveTooltip>.");
  }
  const {
    Portal,
    Positioner,
    Popup,
    Arrow
  } = ctx.isTouchDevice ? PopoverPrimitive : TooltipPrimitive;
  let t5;
  if ($[8] !== className) {
    t5 = cn("relative w-fit max-w-xs overflow-visible rounded bg-foreground px-2.5 py-1.5 text-xs text-background selection:bg-background selection:text-foreground", "origin-[var(--transform-origin)]", "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95", "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95", "motion-reduce:animate-none motion-reduce:transition-none", "data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== Arrow) {
    t6 = <Arrow className="size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />;
    $[10] = Arrow;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children) {
    t7 = <div className="relative z-10">{children}</div>;
    $[12] = children;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== Popup || $[15] !== props || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <Popup data-slot="responsive-tooltip-content" className={t5} {...props}>{t6}{t7}</Popup>;
    $[14] = Popup;
    $[15] = props;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== Positioner || $[21] !== align || $[22] !== alignOffset || $[23] !== side || $[24] !== sideOffset || $[25] !== t8) {
    t9 = <Positioner sideOffset={sideOffset} align={align} alignOffset={alignOffset} side={side} className="pointer-events-auto isolate z-50">{t8}</Positioner>;
    $[20] = Positioner;
    $[21] = align;
    $[22] = alignOffset;
    $[23] = side;
    $[24] = sideOffset;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== Portal || $[28] !== t9) {
    t10 = <Portal>{t9}</Portal>;
    $[27] = Portal;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
export { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger };